import { Card, Text, BlockStack, Banner } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { Endpoints } from '../api/endpoints';
import ProfitChart, { ProfitPoint } from './ProfitChart';
import { usePlan } from '../hooks/usePlan';

interface ForecastResponse {
  history: { date: string; net_profit: number }[];
  forecast: { date: string; net_profit: number }[];
}

/**
 * Profit forecast widget — shows historical net profit plus a projected
 * horizon (plan-gated). PLAN.md §7.1 / §7.5.
 */
export default function ForecastWidget() {
  const plan = usePlan();
  const { data, loading, error } = useApi<ForecastResponse>(Endpoints.reports.forecast);

  if (!plan.limit('forecast_days')) {
    return (
      <Card>
        <Banner tone="info">
          Upgrade your plan to unlock profit forecasting.
        </Banner>
      </Card>
    );
  }

  const points: ProfitPoint[] = [
    ...(data?.history ?? []).map((h) => ({ date: h.date, net_profit: h.net_profit })),
    ...(data?.forecast ?? []).map((f) => ({ date: f.date, net_profit: f.net_profit, forecast: true })),
  ];

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h3" variant="headingMd">
          Profit forecast
        </Text>
        {error && <Banner tone="critical">{error}</Banner>}
        {loading ? (
          <Text as="span" tone="subdued">
            Loading forecast…
          </Text>
        ) : (
          <ProfitChart data={points} showRevenue={false} />
        )}
      </BlockStack>
    </Card>
  );
}
